import React, {
   createContext,
   forwardRef,
   useContext,
   useEffect,
   useImperativeHandle,
   useMemo,
   useRef,
   useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { useMessageItem } from "../MessageItemContext/MessageItemContext";
import {
   addComment,
   getAlerts,
   getForm,
   getSqlFieldValues,
   saveFormV2,
} from "../../services/restAPI/RestApi";
import { Message } from "../../components/BP2Components";
import {
   clearMessageHistory,
   resetAllSqlValues,
   setFormInfo,
   setGridFormRefs,
   setGridRequestFieldId,
} from "../../appRedux/actions/FormsV2";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { executeWorkflowStep, runListAction } from "../../services/api/Api";
import {
   buildFieldEnumMapFromForm,
   isFieldRequired,
   registerAdditionalSaveData,
   transformFormValuesForRequest,
   unregisterAdditionalSaveData,
   validateForm,
} from "../../components/FormsV2/GeneralScripts";
import { mapFormChangelogObject, stringContainsOnly1Char } from "../../util/Scripts";
import { useFastActionsContext } from "../../components/Widgets/InfiniteListWidget/FastActionsContext/FastActionContext";

export type FormSubmitError = { type: "VALIDATION" | "SAVE_ERROR" };

export const FORM_SUBMIT_ERROR: Record<"VALIDATION" | "SAVE_ERROR", FormSubmitError> = {
   VALIDATION: { type: "VALIDATION" },
   SAVE_ERROR: { type: "SAVE_ERROR" },
};

export type FormContextData = {
   formId: number;
   gridFormKeys: any;
   gridRowChanges: any;
   setGridFormKeys: (keys: any) => void;
   setGridRowChanges: (row: any) => void;
   setFormData: (data: any) => void;
   addRowOptions: (rowDataId: any, optionsObject: any) => void;
   rowOptions: any;
   formKey: any;
   formData: any;
   form: any; // Form from antd
   formUuid: string;
   isMessageForm?: boolean;
   handleFormSubmit: (event: any) => void;
   isReadOnly: boolean;
   template?: any;
   defaultValues?: {
      [key: string]: any;
   } | null;
   isSubmitVisible: boolean;
   printable: boolean;
   isLoading: boolean;
   isAnyFieldLoading: boolean;
   setIsAnyFieldLoading: (anyFieldLoading: boolean) => void;
   additionalSaveData: any;
   additionalData?: {
      [key: string]: any;
   } | null;
   refreshForm: () => void;
   hideAttachments?: boolean;
   addSaveCallback: (callback: any) => void;
   loadCallback: any;
   changelog: any;
   setChangelogData: (techName: string, value: any, label: string) => void;
   changeFieldPendingStatus: (status: boolean) => void;
   fieldsInfo: {
      [techName: string]: string;
   };
   setFieldsInfo: (alerts: any) => void;
   reloadFieldOptions: (techname: any, recordId: any) => void;
   fieldOptions: any;
   responseData: any;
   initialData: any;
   pendingRequests: any;
   isNestedForm: boolean;
   fieldEnumMap: Record<string, string>;
};

interface FormProviderRef {
   handleFormSubmit: () => void;
}

interface FormProviderProps {
   children: React.ReactNode;
   formId: number;
   gridFormKeys: any;
   rowOptions: any;
   gridRowChanges: any;
   setGridRowChanges: () => void;
   setFormData: () => void;
   addRowOptions: () => void;
   setIsAnyFieldLoading: () => void;
   formKey: any;
   dataId?: string;
   isReadOnly: boolean;
   form: any;
   printable: boolean;
   actionId?: string | number;
   recordId?: string | number;
   saveCallback?: () => void;
   loadCallback?: any;
   setGridFormKeys?: () => void;
   formUuid?: string;
   formData?: any;
   isMessageForm: boolean;
   defaultValues?: any;
   isSubmitVisible: boolean;
   additionalData?: any;
   additionalSaveData: any;
   hideAttachments: boolean;
   fieldOptions: any;
   reloadFieldOptions: any;
   responseData: any;
   initialData: any;
}

interface SqlOption {
   value?: string | number;
   label: string | number;
}

interface SqlFieldWithSqlValues {
   sqlValues: SqlOption[];
   value?: never;
}
interface SqlFieldWithValue {
   value: SqlOption;
   sqlValues?: never;
}

type SqlFieldData = SqlFieldWithSqlValues | SqlFieldWithValue;

interface AllSQLs {
   [key: string]: SqlFieldData;
}

const FormContext = createContext<FormContextData>({
   formId: 0,
   gridFormKeys: {},
   gridRowChanges: {},
   setGridRowChanges: () => {},
   setGridFormKeys: () => {},
   addRowOptions: () => {},
   setFormData: () => {},
   rowOptions: {},
   formKey: null,
   formData: null,
   form: null,
   formUuid: "",
   isMessageForm: false,
   handleFormSubmit: () => {},
   setIsAnyFieldLoading: () => {},
   isReadOnly: false,
   template: null,
   defaultValues: null,
   isSubmitVisible: true,
   printable: false,
   isLoading: true,
   isAnyFieldLoading: false,
   additionalData: null,
   additionalSaveData: null,
   refreshForm: () => {},
   hideAttachments: false,
   addSaveCallback: () => {},
   loadCallback: null,
   changelog: {},
   setChangelogData: () => {},
   changeFieldPendingStatus: () => {},
   fieldsInfo: {},
   setFieldsInfo: () => {},
   reloadFieldOptions: () => {},
   fieldOptions: {},
   responseData: {},
   initialData: {},
   pendingRequests: {
      current: 0,
   },
   isNestedForm: false,
   fieldEnumMap: {},
});

let FormProvider = forwardRef<FormProviderRef, FormProviderProps>(
   (
      {
         children,
         formId,
         formKey,
         dataId,
         isReadOnly = false,
         form,
         printable,
         actionId,
         recordId,
         loadCallback,
         saveCallback: providedSaveCallback,
         formUuid: providedFormUuid,
         formData: providedFormData,
         isMessageForm = false,
         defaultValues,
         isSubmitVisible = true,
         additionalData,
         additionalSaveData,
         hideAttachments = false,
         initialData,
      },
      ref,
   ) => {
      const [formUuid] = useState(
         isMessageForm ? useMessageItem()?.formUuid : providedFormUuid || uuidv4(),
      );
      const requiredTranslationKey = "field.required";
      const dispatch = useDispatch();
      const intl = useIntl();
      const isNestedForm = !!useContext(FormContext).formUuid;
      const [saveCallback, setSaveCallback] = useState<any>(() => providedSaveCallback);
      const [formData, setFormData] = useState<any>(null);
      const [template, setTemplate] = useState<any>(null);
      const formInfoEntry = useSelector(({ formsv2 }: any) => formsv2.formInfo?.[formUuid]);
      const [formRefresher, setFormRefresher] = useState(false);
      const [changelog, setChangelog] = useState({});
      const [responseData, setResponseData] = useState({});
      const [fieldOptions, setFieldOptions] = useState({});
      const [rowOptions, setRowOptions] = useState({});
      const [gridFormKeys, setGridFormKeys] = useState({});
      const [gridRowChanges, setGridRowChanges] = useState({});
      const [reloadOptionsData, setReloadOptionsData] = useState<any>(null);

      const addRowOptions = (rowDataId, optionsObject) => {
         const allRowOptions = rowOptions;
         allRowOptions[rowDataId] = optionsObject;
         setRowOptions(allRowOptions);
      };

      const normalizeSqlOptions = (optionsArr?: SqlOption[]): SqlOption[] => {
         if (!Array.isArray(optionsArr) || !optionsArr.length) {
            return [];
         }
         return optionsArr
            .map((opt) => {
               if (opt == null) return null;
               if (typeof opt !== "object") {
                  return { label: String(opt), value: String(opt) };
               }
               const label = opt.label;
               // Skip options with null/undefined/object labels — the API
               // returns { label: null } for fields with no value.
               if (label == null || typeof label === "object") return null;
               return {
                  label: String(label),
                  value: typeof opt.value !== "undefined" ? opt.value : label,
               };
            })
            .filter(Boolean) as SqlOption[];
      };

      const handleGetAllSqlResponseAndChoose = (
         res: any,
         fieldTechname: string | null,
         newRecordId: string | number | null,
         formData,
      ): void => {
         const allSQLs: AllSQLs = res?.data?.getAllSqls || {};
         const fieldOptions: Record<string, SqlOption[]> = {};

         Object.entries(allSQLs).forEach(([techname, valueObj]) => {
            if (!valueObj || typeof valueObj !== "object") {
               fieldOptions[techname] = [];
               return;
            }
            fieldOptions[techname] = normalizeSqlOptions(valueObj.sqlValues);
         });
         setFieldOptions(fieldOptions);

         const valuesToSet: Record<string, any> = {};

         Object.entries(allSQLs).forEach(([techname, fieldData]) => {
            const elements = formData?.forms_data?.tab?.[0]?.elements ?? {};
            const foundKey = Object.keys(elements).find(
               (key) => elements[key]?.tech_name === techname,
            );
            const el = foundKey ? elements[foundKey] : null;
            if (!fieldData?.value && (el?.object?.UIP_NUM4 === 1 || el?.object?.UIP_NUM4 === "1")) {
               valuesToSet[techname] = el?.object?.UIP_TEKST3;
               return;
            }

            if ("value" in fieldData && typeof fieldData === "object") {
               const label = (fieldData.value as SqlOption)?.label;
               if (label != null && typeof label !== "object") {
                  valuesToSet[techname] = label;
               }
            }
         });

         if (fieldTechname && newRecordId !== null && newRecordId !== undefined) {
            const newOption = fieldOptions[fieldTechname].find(
               (opt) =>
                  (opt.value !== undefined && String(opt.value) === String(newRecordId)) ||
                  String(opt.label) === String(newRecordId),
            );
            if (newOption) {
               valuesToSet[fieldTechname] = newOption.value;
            }
         }

         if (Object.keys(valuesToSet).length > 0) {
            form.setFieldsValue(valuesToSet);
         }
      };

      const reloadFieldOptions = (fieldTechname, newRecordId) => {
         changeFieldPendingStatus(true);
         getSqlFieldValues(
            reloadOptionsData.defaultFields,
            reloadOptionsData.sqlFields,
            reloadOptionsData.valuesFromForm,
            reloadOptionsData.mainForm?._formKey,
         )
            .then((res) => {
               handleGetAllSqlResponseAndChoose(
                  res,
                  fieldTechname,
                  newRecordId,
                  reloadOptionsData.mainForm,
               );
            })
            .finally(() => {
               changeFieldPendingStatus(false);
            });
      };

      // List of currently pending field requests — ref avoids re-rendering all
      // context consumers on every increment/decrement. The debounced callback
      // derives the boolean `isAnyFieldLoading` which is the only value that
      // actually needs to trigger re-renders.
      const pendingRequestsCountRef = useRef(0);
      const [isAnyFieldLoading, setIsAnyFieldLoading] = useState(false);

      const pendingRequests = useMemo(
         () => ({
            get current() {
               return pendingRequestsCountRef.current;
            },
         }),
         [],
      );

      const {
         currentProcessStep,
         formRefresher: providedFormRefresher,
         setFormContextData,
         isAnyFieldInFormLoading,
         setIsAnyFieldInFormLoading,
         setRequiredLock,
         setAlertLock,
         setIsButtonClicked,
      } = useMessageItem();

      const { tabsVisibility } = useFastActionsContext();

      const fieldEnumMap = useMemo(
         () => buildFieldEnumMapFromForm(formData),
         [formData?.forms_data?.tab],
      );

      const getTransformedFormValues = (enumMap = fieldEnumMap) =>
         transformFormValuesForRequest(form.getFieldsValue(), undefined, undefined, enumMap);

      const [isLoading, setIsLoading] = useState(true);
      const [fieldsInfo, setFieldsInfo] = useState({});

      useEffect(() => {
         if (!formData) {
            setRequiredLock(false);
            return;
         }
         const elements = formData?.forms_data?.tab?.[0]?.elements ?? {};
         const requiredElements: Array<{
            techName: string;
            type: string;
            placeholderLabel: string | undefined;
         }> = [];
         Object.keys(elements).forEach((el) => {
            const element = elements[el];
            if (isFieldRequired(element, form, formData)) {
               requiredElements.push({
                  techName: element?.tech_name,
                  type: element?.type,
                  placeholderLabel:
                     element?.object?.UIP_NUM4 === 1 || element?.object?.UIP_NUM4 === "1"
                        ? element?.object?.UIP_TEKST3
                        : undefined,
               });
            }
         });
         const currentValues = form?.getFieldsValue();
         const hasRequiredAndEmptyField = requiredElements.some(
            ({ techName, type, placeholderLabel }) => {
               const fieldVal = currentValues[techName];
               if (
                  fieldVal === "" ||
                  fieldVal === null ||
                  fieldVal === undefined ||
                  (Array.isArray(fieldVal) && fieldVal.length === 0)
               )
                  return true;
               if (type === "radio" && fieldVal === "0") return true;
               if (
                  (type === "select" || type === "sql") &&
                  placeholderLabel !== undefined &&
                  fieldVal === placeholderLabel
               )
                  return true;
               if (typeof fieldVal === "string" && fieldVal.includes("_")) {
                  const noSpaces = fieldVal.split(" ").join("");
                  if (stringContainsOnly1Char(noSpaces, "_")) return true;
               }
               return false;
            },
         );

         const allTabsHidden =
            tabsVisibility?.size > 0 &&
            Array.from(tabsVisibility.values()).every((isVisible) => !isVisible);

         setRequiredLock(requiredElements.length > 0 && hasRequiredAndEmptyField && !allTabsHidden);
      }, [formData, JSON.stringify(form?.getFieldsValue()), tabsVisibility]);

      useEffect(() => {
         // Action alert locks
         let hasAnyAlert = false;
         Object.keys(fieldsInfo).forEach((techName) => {
            if (fieldsInfo[techName] !== "" && fieldsInfo[techName] !== requiredTranslationKey) {
               hasAnyAlert = true;
            }
         });
         setAlertLock(hasAnyAlert);
      }, [fieldsInfo]);

      // TODO - Remove setting alerts from validation, it should be done before validation in fields onBlur
      window["setFieldsInfo"] = setFieldsInfo;

      const refreshForm = () => {
         setFormRefresher(!formRefresher);
      };

      const setChangelogData = (techName, value, label) => {
         let newFieldData = changelog[techName];
         const newChangelogObject = changelog;
         if (!newFieldData) {
            newFieldData = {};
         }
         // label is added only if anything has changed
         if (label) {
            newFieldData["value"] = value;
            newFieldData["label"] = label;
         } else {
            newFieldData["initial"] = value;
         }
         newChangelogObject[techName] = newFieldData;
         setChangelog(newChangelogObject);
      };

      // This function is used to update the save callback from the child components
      const addSaveCallback = (callback: (...args: any[]) => any) => {
         setSaveCallback((prevCallback) => (...args: any[]) => {
            prevCallback && prevCallback(...args);
            callback(...args);
         });
      };

      const getFormData = async () => {
         let mainForm;
         try {
            if (actionId) {
               const actionKey = formData?._formKey || formKey;
               mainForm = await runListAction(actionId, recordId, actionKey).then(
                  (res) => res.data.actions.mainForm,
               );
               setResponseData(mainForm);
            } else if (!providedFormData) {
               const query = new URLSearchParams(window.location.search);
               const messageId = query.get("actionId");
               mainForm = await getForm(formId, dataId, undefined, messageId).then(
                  (res) => res.data.default.mainForm,
               );
            } else {
               mainForm = providedFormData;
            }
         } catch (error) {
            throw error;
         }

         return mainForm;
      };

      useEffect(() => {
         if (!isNestedForm) {
            setFormContextData(formData);
         }
      }, [JSON.stringify(formData)]);

      useEffect(() => {
         if (formData) {
            form.resetFields();
         }
      }, [formData]);

      const debouncedUpdateLoadingState = useDebouncedCallback((pendingCount: number) => {
         const hasPendingRequests = pendingCount > 0;
         setIsAnyFieldLoading(hasPendingRequests);
         setIsAnyFieldInFormLoading(hasPendingRequests);
      }, 300);

      const handleAllAlerts = (mainForm, enumMap = buildFieldEnumMapFromForm(mainForm)) => {
         const valuesFromForm = {
            ...getTransformedFormValues(enumMap),
            easyForm: true,
            UIF_ID: mainForm?.forms_data?.UIF_ID,
         };

         const formTabs = mainForm?.forms_data?.tab;
         const fieldIds: string[] = [];
         const fieldTechnames = {};
         formTabs.forEach((tab) => {
            Object.keys(tab.elements).forEach((key) => {
               if (tab.elements[key]?.hasAlert) {
                  fieldIds.push(key);
                  fieldTechnames[key] = tab.elements[key].tech_name;
               }
            });
         });
         if (fieldIds.length > 0) {
            changeFieldPendingStatus(true);
            getAlerts(fieldIds, valuesFromForm)
               .then((res) => {
                  const alertListObject = res.data.getAlerts;
                  const convertedAlertObject = {};
                  Object.keys(alertListObject).forEach((key) => {
                     const techName = fieldTechnames[key];
                     if (alertListObject[key]?.length === 0) {
                        convertedAlertObject[techName] = "";
                     } else {
                        convertedAlertObject[techName] = alertListObject[key][0][0];
                     }
                  });
                  setFieldsInfo(convertedAlertObject);
               })
               .finally(() => {
                  changeFieldPendingStatus(false);
               });
         }
      };

      useEffect(() => {
         if (!formId && !providedFormData && !actionId && !recordId) {
            Message.info(intl.formatMessage({ id: "formComplete.provideFormId" }), false);
            return;
         }

         setIsLoading(true);
         setFormData(null);
         // Only reset global Redux state for top-level forms
         // Nested forms (e.g. add-item modal inside dedicated list) must not
         // wipe the parent form's SQL values / grid refs, as that causes the
         // parent to re-initialise and unmount.
         if (!isNestedForm) {
            dispatch(setGridFormRefs([]));
            dispatch(setGridRequestFieldId(null));
            dispatch(resetAllSqlValues());
         }
         changeFieldPendingStatus(true);
         getFormData()
            .then(
               (mainForm: {
                  forms_data: {
                     UIF_ID: any;
                     tab: any;
                     defaultSqlFields: any;
                     sqlFields: any;
                  };
                  template?: any[];
                  _formKey: string;
               }) => {
                  dispatch(
                     setFormInfo({
                        [formUuid]: {
                           formId: mainForm.forms_data.UIF_ID,
                           dataId: recordId || dataId,
                           instanceId: (isMessageForm && currentProcessStep?.instanceId) || null,
                           mainFormRef: form,
                           questionnaireTableAnswers: {},
                        },
                     }),
                  );
                  setFormData(mainForm);
                  setIsLoading(false);
                  loadCallback && loadCallback(mainForm);
                  setFieldsInfo(
                     Object.keys(mainForm?.forms_data?.tab?.[0]?.elements).reduce((acc, key) => {
                        acc[mainForm?.forms_data?.tab?.[0]?.elements[key]?.tech_name] = "";
                        return acc;
                     }, {}),
                  );
                  setTemplate(mainForm?.template?.length ? mainForm.template : undefined);
                  setTimeout(() => {
                     const enumMap = buildFieldEnumMapFromForm(mainForm);
                     const valuesFromForm = {
                        ...getTransformedFormValues(enumMap),
                        easyForm: true,
                        UIF_ID: mainForm?.forms_data?.UIF_ID,
                     };
                     const defaultFields = mainForm?.forms_data?.defaultSqlFields;
                     const sqlFields = mainForm?.forms_data?.sqlFields;

                     const onSqlFieldsResolved = () => {
                        changeFieldPendingStatus(false);
                        handleAllAlerts(mainForm, enumMap);
                     };

                     if (defaultFields?.length > 0 || sqlFields?.length > 0) {
                        setReloadOptionsData({
                           defaultFields,
                           sqlFields,
                           valuesFromForm,
                           mainForm,
                        });
                        getSqlFieldValues(
                           defaultFields,
                           sqlFields,
                           valuesFromForm,
                           mainForm?._formKey,
                        )
                           .then((res) => {
                              handleGetAllSqlResponseAndChoose(res, null, null, mainForm);
                           })
                           .finally(onSqlFieldsResolved);
                     } else {
                        onSqlFieldsResolved();
                     }
                  }, 1000);
               },
            )
            .catch((error) => {
               Message.error(intl.formatMessage({ id: "formComplete.errorLoadingForm" }));
               console.error(error);
               changeFieldPendingStatus(false);
               setIsLoading(false);
            });

         return () => {
            dispatch(clearMessageHistory());
         };
      }, [
         formId,
         dataId,
         currentProcessStep?.stepId,
         recordId,
         actionId,
         formRefresher,
         providedFormData,
         providedFormRefresher,
      ]);

      const changeFieldPendingStatus = (status) => {
         const newCount = Math.max(
            0,
            status === true
               ? pendingRequestsCountRef.current + 1
               : pendingRequestsCountRef.current - 1,
         );
         pendingRequestsCountRef.current = newCount;
         debouncedUpdateLoadingState(newCount);
      };

      const goToTheNextProcessStep = (actionId, comment, recipientId, stepId) => {
         return new Promise(async (resolve, reject) => {
            try {
               const { gridData } = await validateForm(
                  formUuid,
                  //@ts-ignore
                  window?.sysAction,
                  formData,
                  setFieldsInfo,
                  intl,
               );
               const requestData = {
                  ...getTransformedFormValues(),
                  ...additionalData,
                  easyForm: true,
                  UIF_ID: formInfoEntry?.formId ?? formData?.forms_data?.UIF_ID,
                  _formKey: formData?._formKey || formKey,
               };
               if (gridData) requestData.storeItems = JSON.stringify(gridData);
               const stepResponse = await executeWorkflowStep(
                  actionId,
                  requestData,
                  comment,
                  recipientId,
                  stepId,
               );
               const respData = stepResponse?.data?.action;
               let hasBasicAlert = false;
               Object.keys(respData).forEach((key) => {
                  const alertObj = respData[key];
                  if (alertObj?.alert) {
                     hasBasicAlert = true;
                  }
               });
               if (
                  JSON.stringify(respData)?.includes(
                     "api.workflow.actions.validation.form_field_required",
                  ) ||
                  hasBasicAlert
               ) {
                  Object.keys(respData).forEach((key) => {
                     const validationError = respData[key];
                     const hasAlert = validationError.hasOwnProperty("hasAlert");
                     const alertContent = validationError.hasAlert;
                     const maskError = validationError.hasOwnProperty("mask");
                     const isRequired = validationError.hasOwnProperty("isRequired");
                     const basicAlerts = validationError.alert;

                     if (hasAlert && alertContent) {
                        setFieldsInfo((prev) => ({ ...prev, [key]: alertContent }));
                     } else if (maskError) {
                        setFieldsInfo((prev) => ({
                           ...prev,
                           [key]: intl.formatMessage({ id: "field.formatError" }),
                        }));
                     } else if (isRequired) {
                        setFieldsInfo((prev) => ({
                           ...prev,
                           [key]: intl.formatMessage({ id: requiredTranslationKey }),
                        }));
                     } else if (basicAlerts) {
                        setFieldsInfo((prev) => ({ ...prev, [key]: basicAlerts[0]?.toString() }));
                     }
                  });
                  reject();
               } else {
                  resolve(stepResponse);
               }

               // This is used to prevent multiple clicks on the action button. Here, and in the catch block, we are basically setting the display to false (next step).
               setIsButtonClicked(false);
            } catch (error) {
               // some errors like 401 with message "system.api.security.user_logged_out" are not errors - it should work like this
               // for example: by this message we should trigger user logout sequence.
               console.error(error);
               reject(error);

               // the same as above, but whe there is an error
               setIsButtonClicked(false);
            }
         });
      };

      const handleFormSubmit = async () => {
         return new Promise(async (resolve, reject) => {
            try {
               // TODO - Remove setting alerts from validation, it should be done before validation in fields onBlur
               // + remove await below then
               let { isFormValid, gridData } = await validateForm(
                  formUuid,
                  //@ts-ignore
                  window?.sysAction,
                  formData,
                  setFieldsInfo,
                  intl,
               );
               const filteredGridData = {};
               Object.keys(gridRowChanges).forEach((key) => {
                  const keySplit = key.split("_");
                  const frmId = keySplit[0];
                  const index = keySplit[1];
                  if (gridRowChanges[key] === true && gridData?.[frmId]?.[index]) {
                     filteredGridData[frmId] = {
                        ...filteredGridData[frmId],
                        [index]: gridData[frmId][index],
                     };
                  }
               });

               Message.loading(intl.formatMessage({ id: "form.isProcessing" }));
               if (isFormValid) {
                  //Check if any changes were made in form and if grid exist in form - if not, save is not necessary
                  // if (
                  //    Object.keys(mapFormChangelogObject(changelog, false)).length === 0 &&
                  //    Object.keys(gridData).length === 0 &&
                  //    Object.keys(formInfo[formUuid].questionnaireTableAnswers).length === 0 &&
                  //    dataId !== "0" &&
                  //    //@ts-ignore
                  //    projectConfig?.fastFormSave
                  // ) {
                  //    Message.destroy();
                  //    Message.info(intl.formatMessage({ id: "form.saveNotRequired" }));
                  //    resolve(dataId);
                  //    return;
                  // }
                  saveFormV2(
                     formInfoEntry?.formId ?? formData?.forms_data?.UIF_ID,
                     formData?._formKey || formKey,
                     {
                        ...getTransformedFormValues(),
                        ...additionalData,
                     },
                     dataId,
                     currentProcessStep && isMessageForm ? currentProcessStep.instanceId : null,
                     Object.keys(filteredGridData)?.length > 0 ? filteredGridData : undefined,
                  )
                     .then(async (res) => {
                        const changelogBase64 = mapFormChangelogObject(changelog);
                        let comment;
                        if (changelog && changelogBase64 !== "e30=") {
                           // If object is not equal "{}" in base64 string
                           comment = `<span style='font-size:0'>_sysdata_${changelogBase64}_sysdata_</span>`;
                        }

                        if (comment && isMessageForm) {
                           try {
                              await addComment({ instId: currentProcessStep.instanceId }, comment);
                           } catch (error) {
                              reject();
                           }
                        }

                        Message.destroy();
                        Message.success(intl.formatMessage({ id: "form.doneProcessing" }));
                        saveCallback && (await saveCallback(dataId, res));
                        resolve(dataId);
                     })
                     .catch(() => {
                        Message.destroy();
                        Message.error(intl.formatMessage({ id: "form.errorWhileSaving" }));
                        reject(FORM_SUBMIT_ERROR.SAVE_ERROR);
                     });
               } else {
                  Message.destroy();
                  Message.error(intl.formatMessage({ id: "form.invalid" }));
                  reject(FORM_SUBMIT_ERROR.VALIDATION);
               }
            } catch {
               Message.destroy();
               Message.error(intl.formatMessage({ id: "form.errorWhileProcessing" }));
               reject(FORM_SUBMIT_ERROR.SAVE_ERROR);
            }
         });
      };

      useEffect(() => {
         const key = formData?._formKey || formKey;
         if (!key || !additionalSaveData) return;
         const getter =
            typeof additionalSaveData === "function"
               ? additionalSaveData
               : () => additionalSaveData;
         registerAdditionalSaveData(key, getter);
         return () => unregisterAdditionalSaveData(key);
      }, [formData?._formKey, formKey, additionalSaveData]);

      useImperativeHandle(ref, () => ({
         handleFormSubmit,
         goToTheNextProcessStep,
      }));

      return (
         <FormContext.Provider
            value={{
               formId: +formId,
               gridFormKeys,
               gridRowChanges,
               setGridRowChanges,
               setGridFormKeys,
               setFormData,
               addRowOptions,
               rowOptions,
               formKey,
               formData,
               form,
               formUuid,
               isMessageForm,
               handleFormSubmit,
               additionalSaveData,
               isReadOnly,
               template,
               defaultValues,
               isSubmitVisible,
               printable,
               isLoading,
               isAnyFieldLoading,
               setIsAnyFieldLoading,
               refreshForm,
               hideAttachments,
               responseData,
               addSaveCallback,
               loadCallback,
               changelog,
               setChangelogData,
               changeFieldPendingStatus,
               pendingRequests,
               fieldsInfo,
               setFieldsInfo,
               fieldOptions,
               reloadFieldOptions,
               initialData,
               isNestedForm,
               fieldEnumMap,
            }}
         >
            {children}
         </FormContext.Provider>
      );
   },
);

export default FormProvider;

export const useFormContext = () => {
   const context = useContext(FormContext);
   if (!context) throw new Error("You are trying to use Form Context outside the Form Provider");
   return context;
};
